import {
  resolveSessionTargetGroupEntries,
  resolveSessionTargetGroupLabel,
} from "./session-planning-reference-lists.js";
import { resolveReferenceListDefaultId } from "./reference-list-entries.js";
import { resolveGroupRateFromRow } from "./trainer-session-rate.js";

export const TRAINER_RATE_COLUMN_IDS = Object.freeze(["single", "group"]);

const LEGACY_GROUP_ALIASES = Object.freeze({
  youth: ["youth", "children", "childrenYouth"],
  adults: ["adults", "women", "men"],
  children: ["children", "youth", "childrenYouth"],
  childrenYouth: ["childrenYouth", "youth", "children"],
  women: ["women", "adults"],
  men: ["men", "adults"],
});

/**
 * @param {unknown} row
 * @returns {boolean}
 */
export function trainerRateRowHasPositive(row) {
  if (!row || typeof row !== "object") return false;
  const single = Number(row.single);
  const group = Number(row.group);
  return (
    (Number.isFinite(single) && single > 0) ||
    (Number.isFinite(group) && group > 0)
  );
}

/**
 * @param {unknown} tenantReferenceLists
 * @param {(key: string, params?: object) => string} t
 * @returns {{ id: string, label: string, isDefault: boolean }[]}
 */
export function buildTrainerRateTargetGroups(tenantReferenceLists, t) {
  return resolveSessionTargetGroupEntries(tenantReferenceLists).map(
    (entry) => ({
      id: String(entry.id || "").trim(),
      label: resolveSessionTargetGroupLabel(entry, t),
      isDefault: entry.isDefault === true,
    }),
  );
}

/**
 * @param {{ id: string, isDefault?: boolean }[]} targetGroups
 * @returns {string}
 */
export function resolveDefaultTrainerRateTargetGroupId(targetGroups) {
  const list = Array.isArray(targetGroups) ? targetGroups : [];
  const explicit = list.find((row) => row?.isDefault === true);
  if (explicit?.id) return explicit.id;
  return list[0]?.id || "youth";
}

/**
 * @param {string[]} groupIds
 * @returns {Record<string, { single: string, group: string }>}
 */
export function emptyRatesForTargetGroups(groupIds) {
  const out = {};
  for (const id of groupIds) {
    out[id] = { single: "", group: "" };
  }
  return out;
}

function readFlatRateCell(raw, colId) {
  const v = raw?.[colId];
  return v == null || v === "" ? "" : String(v);
}

/**
 * @param {unknown} raw
 * @returns {{ single: string, group: string }}
 */
export function sanitizeFlatStandardRates(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    single: readFlatRateCell(src, "single"),
    group: readFlatRateCell(src, "group"),
  };
}

/**
 * @param {unknown} flat
 */
export function parseFlatStandardRatesForSave(flat) {
  const src = flat && typeof flat === "object" ? flat : {};
  const single = Number(src.single);
  const group = Number(src.group);
  const hasSingle = Number.isFinite(single) && single > 0;
  const hasGroup = Number.isFinite(group) && group > 0;
  if (!hasSingle && !hasGroup) return null;
  return {
    single: hasSingle ? single : null,
    group: hasGroup ? group : null,
  };
}

/**
 * @param {object|null|undefined} period
 * @returns {{ single?: number|null, group?: number|null }|null}
 */
export function resolveFlatStandardRates(period) {
  if (!period || typeof period !== "object") return null;
  const parsed = parseFlatStandardRatesForSave(period.flat_standard_rates);
  if (parsed) return parsed;
  const stdId = String(period.standard_target_group_id || "").trim();
  const rates =
    period.rates && typeof period.rates === "object" ? period.rates : {};
  const legacyRow = stdId ? rates[stdId] : null;
  if (!trainerRateRowHasPositive(legacyRow)) return null;
  const single = Number(legacyRow.single);
  const group = Number(legacyRow.group);
  return {
    single: Number.isFinite(single) && single > 0 ? single : null,
    group: Number.isFinite(group) && group > 0 ? group : null,
  };
}

/**
 * UI/Load: fehlende flat_standard_rates aus Legacy-Standard-Zielgruppe ableiten.
 *
 * @param {object|null|undefined} period
 * @param {string[]} groupIds
 * @param {string} defaultTargetGroupId
 */
export function ensureFlatStandardRatesOnPeriod(
  period,
  groupIds,
  defaultTargetGroupId,
) {
  const normalized = normalizeTrainerRatePeriod(
    period,
    groupIds,
    defaultTargetGroupId,
  );
  if (parseFlatStandardRatesForSave(normalized.flat_standard_rates)) {
    return normalized;
  }
  const flatFromLegacy = resolveFlatStandardRates(normalized);
  if (flatFromLegacy) {
    return {
      ...normalized,
      flat_standard_rates: {
        single:
          flatFromLegacy.single != null ? String(flatFromLegacy.single) : "",
        group: flatFromLegacy.group != null ? String(flatFromLegacy.group) : "",
      },
      apply_standard_to_other_groups: true,
    };
  }
  return {
    ...normalized,
    flat_standard_rates: sanitizeFlatStandardRates(period?.flat_standard_rates),
    apply_standard_to_other_groups:
      normalized.apply_standard_to_other_groups !== false,
  };
}

function readRateCell(raw, colId) {
  if (colId === "single") {
    const v = raw?.single;
    return v == null || v === "" ? "" : String(v);
  }
  const direct = raw?.group;
  if (direct != null && String(direct).trim() !== "") return String(direct);
  const resolved = resolveGroupRateFromRow(raw);
  return resolved != null ? String(resolved) : "";
}

/**
 * @param {unknown} raw
 * @param {string[]} groupIds
 * @returns {Record<string, { single: string, group: string }>}
 */
export function sanitizeTrainerPeriodRates(raw, groupIds) {
  const ids = Array.isArray(groupIds) ? groupIds.filter(Boolean) : [];
  const base = emptyRatesForTargetGroups(ids);
  const src = raw && typeof raw === "object" ? raw : {};

  for (const groupId of ids) {
    let row =
      src[groupId] && typeof src[groupId] === "object" ? src[groupId] : null;
    if (!row) {
      const aliases = LEGACY_GROUP_ALIASES[groupId] || [];
      const aliasKey = aliases.find(
        (k) =>
          src[k] &&
          typeof src[k] === "object" &&
          trainerRateRowHasPositive(src[k]),
      );
      row = aliasKey ? src[aliasKey] : {};
    }
    base[groupId] = {
      single: readRateCell(row, "single"),
      group: readRateCell(row, "group"),
    };
  }
  return base;
}

/**
 * @param {object|null|undefined} period
 * @param {string[]} groupIds
 * @param {string} defaultTargetGroupId
 */
export function normalizeTrainerRatePeriod(
  period,
  groupIds,
  defaultTargetGroupId,
) {
  const ids = Array.isArray(groupIds) ? groupIds.filter(Boolean) : [];
  const rates = sanitizeTrainerPeriodRates(period?.rates, ids);
  const fallbackStandard = defaultTargetGroupId || ids[0] || "youth";
  let standardId = String(period?.standard_target_group_id || "").trim();
  if (!standardId || !ids.includes(standardId)) {
    standardId = ids.includes(fallbackStandard)
      ? fallbackStandard
      : ids[0] || fallbackStandard;
  }

  const legacyFilled = ["youth", "adults"].filter(
    (id) => ids.includes(id) && trainerRateRowHasPositive(rates[id]),
  );
  let applyStandard =
    period?.apply_standard_to_other_groups !== false &&
    period?.apply_standard_to_other_groups !== "false";
  const hasFlat = Boolean(
    parseFlatStandardRatesForSave(period?.flat_standard_rates),
  );
  if (hasFlat) {
    applyStandard = true;
  } else if (
    period?.apply_standard_to_other_groups == null &&
    legacyFilled.length >= 2
  ) {
    applyStandard = false;
  }

  return {
    valid_from: String(period?.valid_from || "")
      .trim()
      .slice(0, 10),
    valid_to:
      period?.valid_to != null && String(period.valid_to).trim() !== ""
        ? String(period.valid_to).trim().slice(0, 10)
        : "",
    standard_target_group_id: standardId,
    apply_standard_to_other_groups: applyStandard,
    flat_standard_rates: sanitizeFlatStandardRates(period?.flat_standard_rates),
    rates,
  };
}

/**
 * @param {Record<string, { single?: string|number, group?: string|number }>} rates
 * @param {string[]} groupIds
 */
export function parseTrainerPeriodRatesForSave(rates, groupIds) {
  const out = {};
  for (const groupId of groupIds) {
    const single = Number(rates?.[groupId]?.single);
    const grp = Number(rates?.[groupId]?.group);
    out[groupId] = {
      single: Number.isFinite(single) && single > 0 ? single : null,
      group: Number.isFinite(grp) && grp > 0 ? grp : null,
    };
  }
  return out;
}

/**
 * @param {object|null|undefined} period
 * @param {string} targetGroupId
 * @returns {{ single?: number|null, group?: number|null }|null}
 */
export function resolveEffectiveTrainerRateRow(period, targetGroupId) {
  const tg = String(targetGroupId || "").trim();
  if (!tg || !period) return null;
  const rates =
    period?.rates && typeof period.rates === "object" ? period.rates : {};
  const direct = rates[tg];
  if (trainerRateRowHasPositive(direct)) return direct;

  if (period.apply_standard_to_other_groups !== false) {
    const flatRow = resolveFlatStandardRates(period);
    if (trainerRateRowHasPositive(flatRow)) return flatRow;

    const stdId = String(period.standard_target_group_id || "").trim();
    if (stdId && stdId !== tg) {
      const stdRow = rates[stdId];
      if (trainerRateRowHasPositive(stdRow)) return stdRow;
    }
  }

  const aliases = LEGACY_GROUP_ALIASES[tg] || [];
  for (const alias of aliases) {
    const row = rates[alias];
    if (trainerRateRowHasPositive(row)) return row;
  }
  return null;
}

/**
 * @param {object|null|undefined} period
 * @param {string[]} groupIds
 */
export function trainerRatePeriodHasPositiveRate(period, groupIds) {
  const ids = Array.isArray(groupIds) ? groupIds : [];
  for (const groupId of ids) {
    if (
      trainerRateRowHasPositive(resolveEffectiveTrainerRateRow(period, groupId))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * @param {unknown} tenantReferenceLists
 * @returns {string}
 */
export function resolveReferenceListDefaultTargetGroupId(tenantReferenceLists) {
  const entries = resolveSessionTargetGroupEntries(tenantReferenceLists);
  return resolveReferenceListDefaultId(entries) || "youth";
}
